import ModalInformation from "@/app/components/ui/Modals/Information";
import TableSkeleton from "@/app/components/ui/Skeletons/TableSkeleton";
import { useStaffCalendarBookingDetailsAPI } from "@/app/hooks/api_hooks/StaffCalendar/useStaffCalendarBookingDetailsAPI";
import { useModalState } from "@/app/hooks/ui_hooks/useModalState";
import { useLoader } from "@/app/hooks/useLoader";
import { useRouter } from "@/app/hooks/useRouter";
import { useUtils } from "@/app/hooks/useUtils";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { isArray, isObject } from "lodash";
import { useEffect } from "react";

interface ICalendarBlockDetailsModalState {
  bookingId: string;
}
const CalendarBlockDetailsModal = () => {
  const { getRouteKey } = useRouter();
  const { bookingId } = useModalState("CALENDAR_BLOCK_DETAILS_MODAL") as ICalendarBlockDetailsModalState;
  const { bookingDetails: staffBookingDetails, execute: fetchBookingDetails } = useStaffCalendarBookingDetailsAPI();
  const { convertNoteToArray, veticDateFormat, convertTo12HourFormatWithAMPM } = useUtils();
  const isBookingDetailsLoading = useLoader("isStaffCalendarBookingDetailsLoadingInProgress");

  const staffName = isArray(staffBookingDetails?.staff_details)
    ? staffBookingDetails?.staff_details?.map((staff) => staff?.name)?.filter(Boolean)?.join(", ")
    : isObject(staffBookingDetails?.staff_details)
      ? staffBookingDetails?.staff_details?.["name"]
      : "-";
  const noteItems = staffBookingDetails?.appointment_note ? convertNoteToArray(staffBookingDetails?.appointment_note) : [];
  const bookingStatus = (staffBookingDetails?.booking_status ?? "NA")?.toLowerCase();

  const bookingStatusClassName = clsx(
    "rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide",
    bookingStatus === "booked" && "border-blue-200 bg-blue-50 text-blue-700",
    bookingStatus === "completed" && "border-emerald-200 bg-emerald-50 text-emerald-700",
    bookingStatus === "cancelled" && "border-rose-200 bg-rose-50 text-rose-700",
    bookingStatus === "rescheduled" && "border-amber-200 bg-amber-50 text-amber-700",
    !["booked", "completed", "cancelled", "rescheduled"].includes(bookingStatus) &&
    "border-gray-200 bg-gray-50 text-gray-700",
  );

  const handleOpenCustomerProfile = () => {
    if (!staffBookingDetails?.user_id || !staffBookingDetails?.pet_id) return;
    window.open(
      `${getRouteKey("APPOINTMENTS_TAB", "url")}/${staffBookingDetails?.user_id}/${staffBookingDetails?.pet_id}`,
      "_blank",
    );
  };

  useEffect(() => {
    if (bookingId) {
      fetchBookingDetails(bookingId);
    }
  }, [bookingId]);
  return (
    <ModalInformation
      modalId="CALENDAR_BLOCK_DETAILS_MODAL"
      backdropBlur={false}

      dialogClassName="w-[calc(100vw-2rem)] !translate-x-0 sm:my-8 sm:w-full sm:max-w-2xl sm:p-6"
      title={
        <div className="flex w-full flex-wrap items-start justify-between gap-2 pb-1 pr-1 sm:gap-3">
          <div>
            {/* <p className="text-sm font-semibold text-gray-900">{staffName}</p> */}
            <p className="mt-0.5 text-xs text-gray-600">
              {convertTo12HourFormatWithAMPM(staffBookingDetails?.booking_start_time)} -{" "}
              {convertTo12HourFormatWithAMPM(staffBookingDetails?.booking_end_time)}
            </p>
          </div>
          <span className={bookingStatusClassName}>{staffBookingDetails?.booking_status ?? "NA"}</span>
        </div>
      }
    >
      <div>
        <div className="max-h-[70dvh] overflow-y-auto bg-white px-1 pb-4 pt-3 sm:px-1 sm:pb-1">
          {!isBookingDetailsLoading ? (
            staffBookingDetails ? (
              <div className="space-y-3">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="rounded-xl border border-gray-200 bg-[#f8fafc] p-3">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Customer</p>
                    <div className="mt-1 flex items-center gap-2">
                      <p className="text-sm font-medium text-gray-900">{staffBookingDetails?.user_details?.name ?? "-"}</p>
                      <button type="button" onClick={handleOpenCustomerProfile}>
                        <ArrowTopRightOnSquareIcon className="h-4 w-4 cursor-pointer text-[#0068FF]" />
                      </button>
                    </div>
                    <p className="mt-1 text-xs text-gray-600">{staffBookingDetails?.user_details?.mobile ?? "-"}</p>
                  </div>

                  <div className="rounded-xl border border-gray-200 bg-[#f8fafc] p-3">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Pet</p>
                    <p className="mt-1 text-sm font-medium text-gray-900">{staffBookingDetails?.pet_details?.name ?? "-"}</p>
                    <p className="mt-1 text-xs text-gray-600">
                      {staffBookingDetails?.pet_details?.pet_species_name ?? "-"}
                      {staffBookingDetails?.pet_details?.pet_breed ? `, ${staffBookingDetails?.pet_details?.pet_breed}` : ""}
                    </p>
                  </div>
                </div>

                <div className="rounded-xl border border-gray-200 p-3">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Service</p>
                  <p className="mt-1 text-sm font-medium text-gray-900">{staffBookingDetails?.service_details?.service_type ?? "-"}</p>
                  <p className="mt-1 text-xs text-gray-600">{staffBookingDetails?.subservice_details?.name ?? "-"}</p>
                </div>

                <div className="rounded-xl border border-gray-200 p-3">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Comments</p>
                  {noteItems?.length > 0 ? (
                    <div className="mt-1 space-y-1">
                      {noteItems.map((item, id) => {
                        return (
                          <p className="text-xs text-gray-700" key={id}>
                            {item}
                          </p>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="mt-1 text-xs text-gray-500">No comments</p>
                  )}
                </div>

                <div className="rounded-xl border border-gray-200 p-3">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Audit Trail</p>
                  <div className="mt-2 grid grid-cols-1 gap-2 text-xs text-gray-700">
                    <p>
                      Created by <span className="font-medium text-[#0068FF]">{staffBookingDetails?.created_by ?? "-"}</span>{" "}
                      on {veticDateFormat(staffBookingDetails?.created_at)}
                    </p>
                    <p>
                      Edited by <span className="font-medium text-[#0068FF]">{staffBookingDetails?.updated_by ?? "-"}</span> on{" "}
                      {veticDateFormat(staffBookingDetails?.updated_at)}
                    </p>
                    <p>
                      Created from <span className="font-medium text-[#0068FF]">{staffBookingDetails?.created_from ?? "-"}</span>
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-blue-500 text-sm text-center">No Data Found</p>
            )
          ) : (
            <TableSkeleton />
          )}
        </div>
      </div>
    </ModalInformation>
  );
};

export default CalendarBlockDetailsModal;
